import React from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";

import * as QueriesQL from "queries/site";
import SitePublicSchema from "@common/schema/site/public";
import * as Section from "@repoflow/components/section";
import { Crumb } from "@repoflow/components/breadcrumb";
import { Nav } from "@repoflow/components/nav";
import { Container, Row, Col } from "reactstrap";

import * as PagesRoute from "./pages";
import * as SectionsRoute from "./sections";
import * as DomainsRoute from "./domains";
import * as FilesRoute from "./files";
import * as PacksRoute from "./packs";
import * as AgentsRoute from "./agents";
import * as BlogRoute from "./blog";
import * as ViewRoute from "./view";
import * as RemoveRoute from "./remove";

// The site, and everything it owns.
//
// This route is where `siteId` enters the app, and every query beneath it takes
// that id as a variable — which is what makes the nesting the same boundary on
// the client that it is on the API.
const LocalProvider = ({
  match: {
    params,
    params: { siteId },
  },
  children: Children,
}) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/${siteId}`;
  const basepath = `${parentBasepath}/:siteId`;

  // Keyed on the entity, which is what the accidental remount used to do.
  //
  // Every screen below seeds its form state ONCE, from the entity this
  // route resolved. Until the render prop above was called rather than
  // rendered, React saw a new component type on every render and remounted
  // the lot — which reset those forms, and happened to be what made moving
  // from one siteId to another show the new one's values. It no longer
  // remounts for free, so the reset it was providing is asked for here.
  return (
    <Section.Provider
      key={siteId}
      schema={SitePublicSchema}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  return (
    <LocalProvider {...props}>
      {({ params }) => (
        <Section.Query
          query={QueriesQL.get}
          variables={params}
          datapath={"sites.get"}
        >
          {ContentHandler}
        </Section.Query>
      )}
    </LocalProvider>
  );
}

function ContentHandler({ base, basepath }) {
  // Hidden once you are inside one of the site's rows. A page's screens — its
  // view, its editor, its remove — and a section's are where you
  // work on that entity, and the site's own navigation next to that entity's
  // own actions is the same mis-click hazard that kept Edit off this bar. The
  // breadcrumb is the way back out, and it is the one that names the row you
  // are in.
  //
  // `new` is deliberately not one of them: the create form is reached from a
  // list and belongs to the site the way the list does, so the tabs stay.
  const inside = useRouteMatch({
    path: `${basepath}/:kind(page|section)/:entityId`,
  });
  // A blog post's and a collection's screens are one level deeper, and carry
  // their own Edit and Remove for the same reason a page's do. A template's
  // editor is the same depth and wants the width the way a section's does.
  const insideBlog = useRouteMatch({
    path: `${basepath}/blog/:kind(post|collection|template)/:entityId`,
  });
  const insideEntity =
    (Boolean(inside) && inside.params.entityId !== "new") ||
    (Boolean(insideBlog) && insideBlog.params.entityId !== "new");

  return (
    <>
      {/* Everything a site owns, and the only way to reach its sections.
          Without this the sections screens exist at a URL nobody is given —
          the shell sidebar stops at /site and the breadcrumb only walks back.

          What is NOT here is Remove. This bar used to render on every screen
          inside the site, including a page's and a section's, each of which has
          a Remove of its own; two buttons a click apart meaning different rows
          is a way to lose work, and the first e2e run after that bar carried an
          Edit clicked the wrong one and saved the SITE's title over the page's.
          It lives on the site's own screen now, the way every other entity in
          the app carries it — and the bar itself steps aside inside a row, per
          `insideEntity` above. */}
      {!insideEntity && (
        <Container className="mt-4">
          <Row>
            <Col>
              <Nav
                // Named so a spec can say "the site's tabs" without matching
                // the breadcrumb's links or the page editor's own tab strip,
                // which carry some of the same words.
                id="site-tabs"
                tabs
                // `base`, not `basepath`. Nav uses `route` for BOTH the active
                // check and the navigation, so a pattern here sends the browser
                // to the literal "/site/:siteId/section" — which renders, because
                // the SPA serves the shell for any path, and then resolves a site
                // called ":siteId" that does not exist.
                sections={[
                  // Pages first, because it is where creating a site lands you
                  // and what you come back to; the site's own screen is where
                  // you go once, to set the document up, so it is last.
                  { label: "Pages", route: `${base}/page` },
                  { label: "Sections", route: `${base}/section` },
                  { label: "Blog", route: `${base}/blog` },
                  { label: "Domains", route: `${base}/domain` },
                  // The bytes behind this one are in bucketws, not here — see
                  // ./files. It sits after Domains and before Configuration
                  // because it is content, and Configuration is the one you
                  // visit once.
                  { label: "Files", route: `${base}/file` },
                  // After Files and before Configuration, because a pack is
                  // content too — it is a route the tenant fills with an
                  // uploaded bundle rather than with elements. It competes
                  // with Pages for the same URL space, which is the argument
                  // for it being a section of its own rather than a panel.
                  { label: "Route packs", route: `${base}/pack` },
                  // Who else may work on this site: agent keys bound to it.
                  // Before Configuration, which stays last as the screen you
                  // visit once.
                  { label: "Agents", route: `${base}/agent` },
                  // "Configuration", not "Site": the screen behind it is the
                  // document every page of the site is composed into — head,
                  // body template, injected style and script — with the site's
                  // own title as its first field. A tab called Site inside a
                  // site said only that you were where you already were.
                  //
                  // `exact`, because this route is a prefix of every other tab's
                  // and would otherwise read as active on all of them.
                  { label: "Configuration", route: `${base}`, exact: true },
                ]}
              />
            </Col>
          </Row>
        </Container>
      )}

      <Switch>
        <Route path={`${basepath}/remove`} component={RemoveRoute.Content} />
        <Route path={`${basepath}/page`} component={PagesRoute.Content} />
        <Route path={`${basepath}/section`} component={SectionsRoute.Content} />
        <Route path={`${basepath}/blog`} component={BlogRoute.Content} />
        <Route path={`${basepath}/domain`} component={DomainsRoute.Content} />
        <Route path={`${basepath}/file`} component={FilesRoute.Content} />
        <Route path={`${basepath}/pack`} component={PacksRoute.Content} />
        <Route path={`${basepath}/agent`} component={AgentsRoute.Content} />
        <Route path={`${basepath}`} component={ViewRoute.Content} />
      </Switch>
    </>
  );
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath, params: { siteId } }) => (
        <>
          {/* No useCrumbLabel: a site's id IS its name, so fetching to render
              what is already in the URL would be a query per navigation for no
              new information. */}
          <Crumb to={`${base}`} label={siteId} />

          <Switch>
            <Route path={`${basepath}/remove`} component={RemoveRoute.Indice} />
            <Route path={`${basepath}/page`} component={PagesRoute.Indice} />
            <Route path={`${basepath}/section`} component={SectionsRoute.Indice} />
            <Route path={`${basepath}/blog`} component={BlogRoute.Indice} />
            <Route path={`${basepath}/domain`} component={DomainsRoute.Indice} />
            <Route path={`${basepath}/file`} component={FilesRoute.Indice} />
            <Route path={`${basepath}/pack`} component={PacksRoute.Indice} />
            <Route path={`${basepath}/agent`} component={AgentsRoute.Indice} />
            <Route path={`${basepath}`} component={ViewRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => (
  <>
    <ViewRoute.Sidebar />
    <PagesRoute.Sidebar />
    <SectionsRoute.Sidebar />
  </>
);
